import React, {useState, useEffect} from 'react';
import {Modal, Button, Form, Spinner, Alert} from 'react-bootstrap';
import {VacationMessage} from '../DomainEmailAccounts';

interface OutOfOfficeModalProps {
  show: boolean;
  onHide: () => void;
  accountEmail: string;
  messageAbsence?: VacationMessage | null;
  onSave: (messageAbsence: {subject: string; body: string; email: string; active: boolean}) => Promise<void>;
}

const DEFAULT_SUBJECT = 'Absence du bureau';
const DEFAULT_BODY = `Bonjour,

Je serai absent(e) du <date> jusqu'au <date>.

Je prendrai connaissance de votre email à mon retour.

Cordialement,



<Prénom> <Nom>`;

const OutOfOfficeModal: React.FC<OutOfOfficeModalProps> = ({
  show,
  onHide,
  accountEmail,
  messageAbsence,
  onSave,
}) => {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [actif, setActif] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState('');

  useEffect(() => {
    if (show) {
      if (messageAbsence) {
        setSubject(messageAbsence.subject?.trim() || DEFAULT_SUBJECT);
        setBody(messageAbsence.body?.trim() || DEFAULT_BODY);
        setActif(messageAbsence.active || false);
      } else {
        setSubject(DEFAULT_SUBJECT);
        setBody(DEFAULT_BODY);
        setActif(false);
      }
      setApiError('');
    }
  }, [messageAbsence, show]);

  const handleClose = () => {
    if (!isLoading) {
      onHide();
    }
  };

  const handleSave = async () => {
    setIsLoading(true);
    setApiError('');

    try {
      await onSave({
        subject: subject.trim(),
        body: body.trim(),
        email: accountEmail,
        active: actif,
      });
      handleClose();
    } catch (error: any) {
      console.error('Erreur lors de la sauvegarde du message d\'absence:', error);

      if (error.response?.data?.message) {
        setApiError(error.response.data.message);
      } else {
        setApiError('Erreur lors de la sauvegarde du message d\'absence. Veuillez réessayer');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fa-solid fa-calendar-xmark me-2 text-info"></i>
          Gestion du message d'absence
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {apiError && (
          <Alert variant="danger" dismissible onClose={() => setApiError('')} className="mb-3">
            <i className="fa-solid fa-circle-exclamation me-2"></i>
            {apiError}
          </Alert>
        )}

        <div className="alert alert-info mb-3">
          <i className="fa-solid fa-info-circle me-2"></i>
          Compte : <strong>{accountEmail}</strong>
        </div>

        <div className="mb-4">
          <Form.Label className="mb-2">
            <strong>Statut du message d'absence</strong>
          </Form.Label>
          <div className="d-flex align-items-center gap-3 p-3 bg-light rounded">
            <Form.Check
              type="switch"
              id="vacation-status-switch"
              checked={actif}
              onChange={(e) => setActif(e.target.checked)}
              label=""
              disabled={isLoading}
            />
            <div>
              <strong>{actif ? 'Actif' : 'Inactif'}</strong>
              <p className="mb-0 text-muted small">
                {actif
                  ? 'Le message automatique est actuellement activé'
                  : 'Le message automatique est actuellement désactivé'}
              </p>
            </div>
          </div>
        </div>

        <Form>
          <Form.Group className="mb-3">
            <Form.Label>
              Sujet {actif && <span className="text-danger">*</span>}
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="Ex: Absent du bureau"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              disabled={!actif || isLoading}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              Message {actif && <span className="text-danger">*</span>}
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={10}
              placeholder="Saisissez votre message d'absence..."
              value={body}
              onChange={(e) => setBody(e.target.value)}
              disabled={!actif || isLoading}
              style={{whiteSpace: 'pre-wrap'}}
            />
            <Form.Text className="text-muted">
              Ce message sera envoyé automatiquement en réponse aux emails reçus
            </Form.Text>
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-between">
        <Button variant="secondary" onClick={handleClose} disabled={isLoading}>
          <i className="fa-solid fa-xmark me-2"></i>
          Annuler
        </Button>
        <Button 
          variant="primary" 
          onClick={handleSave} 
          disabled={isLoading || (actif && (!subject.trim() || !body.trim()))}
        >
          {isLoading ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
              Enregistrement en cours...
            </>
          ) : (
            <>
              <i className="fa-solid fa-save me-2"></i>
              Enregistrer
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default OutOfOfficeModal;
